import type { ComputedRef, Ref } from "vue";
import type { Shop } from "~/types/shop";
import { useDemoAccueil, useDonneeDemoAccueil } from "~/composables/useDemoAccueil";

/**
 * useShopPublicData — fetch SSR + disponibilité de la boutique pour la page
 * publique /boutique/[slug]. Calqué sur `useEventPublicData` (/b/[slug]).
 *
 * « Boutique non disponible » UNIQUEMENT sur 404 explicite. Erreurs
 * transitoires (timeout/5xx/network) → skeleton + retry au lieu d'écran rouge.
 */
export async function useShopPublicData(
  slug: ComputedRef<string> | Ref<string>,
  isPreview: ComputedRef<boolean> | Ref<boolean>,
) {
  // Avant tout `await` : useI18n() exige le contexte de setup.
  const { t } = useI18n();
  const config = useRuntimeConfig();

  const demo = useDemoAccueil().estIntegree;
  const fetchUrl = computed(() =>
    urlApiPublique(`${config.public.apiBaseUrl}/api/public/shops/${slug.value}`, {
      preview: isPreview.value,
      demo,
    }),
  );
  // ⚠️ Pas de `await` inline : les computed sont créés de façon SYNCHRONE
  // (avant tout await) pour rester rattachés au scope du composant.
  // L'`await` SSR est fait tout en bas du composable.
  const shopAsync = useFetch<{ success: boolean; data: Shop }>(
    fetchUrl.value,
    {
      // La clé PORTE le mode aperçu : sans lui, la charge SSR mise en cache
      // pour la page publique serait réutilisée dans l'iframe d'aperçu (et
      // inversement), et une boutique en brouillon resterait invisible.
      // Démo de la home : clé à part, rendu du serveur repris (cf. useDonneeDemoAccueil).
      ...useDonneeDemoAccueil(`shop-${slug.value}${isPreview.value ? "-preview" : ""}`),
      server: true,
      lazy: false,
      // Retry transitoire (timeout/5xx) pour éviter le flaky « Boutique non
      // disponible » au premier refresh (pattern /b, Karim 2026-06-21).
      retry: 2,
      retryDelay: 300,
      timeout: 8000,
    },
  );
  const { data: shopData, error: fetchError, pending: isLoadingShop } = shopAsync;

  // APERÇU TEMPS RÉEL — le dashboard pousse le brouillon en cours d'édition
  // par postMessage ; il se superpose aux données enregistrées. Inerte hors
  // `?preview=true`. Parité adhésion / billetterie. Karim 2026-08-30.
  const draftPreview = useEntityDraftPreview({
    isPreviewMode: computed(() => isPreview.value === true),
  });

  const shop = computed(() => {
    const ssr = shopData.value?.data ?? null;
    const merged = draftPreview.mergeEntity(ssr);
    if (!merged) return null;
    // Les produits sont une LISTE : un produit retiré côté dashboard doit
    // disparaître de l'aperçu, d'où le remplacement plutôt qu'une fusion de
    // surface. Chaque produit reste fusionné sur son homologue en base (même
    // `id`) pour garder ce que seul le serveur sait (stock, images résolues).
    const draftProducts = draftPreview.overrides.value.tarifs;
    if (!draftProducts) return merged;
    return {
      ...merged,
      products: draftPreview.mergeTarifs(merged.products ?? []),
    };
  });
  const isInitialLoading = computed(() => isLoadingShop.value && !shop.value);

  const isEntityMissing = computed(() => {
    if (shop.value) return false;
    if (!fetchError.value) return false;
    const status = (fetchError.value as { statusCode?: number; status?: number }).statusCode
      ?? (fetchError.value as { statusCode?: number; status?: number }).status;
    return status === 404;
  });

  // Raison réelle du 404 exposée par le backend (draft / archived / not_found)
  // → on affiche la VÉRITÉ au lieu d'un message générique (pattern /b).
  const missingReason = computed<string>(() => {
    const data = (fetchError.value as { data?: { reason?: string } } | null)?.data;
    return data?.reason ?? "not_found";
  });
  const missingCopy = computed(() => {
    switch (missingReason.value) {
      case "archived":
        return {
          icon: "i-lucide-archive",
          title: t("boutique.publicData.fermeeTitre"),
          message:
            t("boutique.publicData.fermeeMessage"),
        };
      case "draft":
        return {
          icon: "i-lucide-clock",
          title: t("boutique.publicData.pasPublieeTitre"),
          message:
            t("boutique.publicData.pasPublieeMessage"),
        };
      default:
        return {
          icon: "i-lucide-store",
          title: t("boutique.publicData.introuvableTitre"),
          message:
            t("boutique.publicData.introuvableMessage"),
        };
    }
  });

  // Await SSR (cf. note plus haut).
  await shopAsync;

  return {
    shop,
    isInitialLoading,
    isEntityMissing,
    missingCopy,
  };
}
